import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import icon from '../../assets/support-icon3.svg';
import next from '../../assets/next.svg';

interface Notice {
  _id: string;
  category: string;
  title: string;
  content: string;
  img: string;
  viewCount: number;
  startDate: string;
  endDate: string;
  createdAt: string;
}

const NoticeList: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [noticeList, setNoticeList] = useState<Notice[]>([]);
  const [offset, setOffset] = useState(0); // offset: 이미 로드한 개수
  const [limit, setLimit] = useState(3); // limit: 한 번에 가져올 개수 (최초 3개)
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [prevFilter, setPrevFilter] = useState('all');
  const [filter, setFilter] = useState('all');

  const getCategoryKorean = (category: string) => {
    const categoryMap: { [key: string]: string } = {
      general: '일반',
      event: '이벤트',
      update: '업데이트',
      etc: '기타',
    };
    return categoryMap[category] || category;
  };

  const fetchNoticeList = useCallback(async () => {
    if (loading) return;
    try {
      setLoading(true);
      const response = await axios.get('/api/notice/list', {
        params: { offset, limit, filter, prevFilter },
      });
      const { data, hasMore: more, nextOffset } = response.data;
      setNoticeList(prev => [...prev, ...data]);
      setOffset(nextOffset);
      setHasMore(more);
    } catch (error) {
      console.error('Notice 불러오기 오류:', error);
    } finally {
      setLoading(false);
    }
  }, [offset, limit, filter, loading]);

  useEffect(() => {
    // 필터 바뀌면 초기화만
    setNoticeList([]);
    setOffset(0);
    setHasMore(true);
  }, [filter]);

  useEffect(() => {
    // offset이 0일 때만 새로운 필터로 첫 페이지 로드
    if (offset === 0) {
      fetchNoticeList();
    }
  }, [offset, filter]);

  return (
    <div className="notice-container">
      <div className="page-header">
        <img src={icon} alt="Mind Thermometer Icon" className="page-header__icon" />
        <h2 className="page-header__title">공지사항</h2>
        <p>공지사항을 확인하세요.</p>
      </div>
      <div className="notice-list">
        <div className="notice-list__header">
          {user?.role === 'admin' && (
            <button onClick={() => navigate('/setting/notice/new')}>작성하기</button>
          )}
        </div>

        <div className="notice-list__filter">
          <button
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            전체
          </button>
          <button
            className={filter === 'general' ? 'active' : ''}
            onClick={() => setFilter('general')}
          >
            일반
          </button>
          <button
            className={filter === 'event' ? 'active' : ''}
            onClick={() => setFilter('event')}
          >
            이벤트
          </button>
          <button
            className={filter === 'update' ? 'active' : ''}
            onClick={() => setFilter('update')}
          >
            업데이트
          </button>
          <button
            className={filter === 'etc' ? 'active' : ''}
            onClick={() => setFilter('etc')}
          >
            기타
          </button>
        </div>

        {noticeList.length > 0 ? (
          <div className="notice-list__list">
            {noticeList.map(notice => (
              <div
                className="notice-list__item"
                key={notice._id}
                onClick={() => navigate(`/setting/notice/${notice._id}`)}
              >
                <div className="notice-list__title-date">
                  <span className="notice-list__category">
                    {getCategoryKorean(notice.category)}
                  </span>
                  <h3 className="notice-list__title">{notice.title}</h3>
                  <p className="notice-list__date">
                    {moment(notice.createdAt).format('YYYY-MM-DD')}
                  </p>
                </div>
                <div>
                  <button className="notice-list__view-btn">
                    <img src={next} alt="이동" />
                  </button>
                </div>
              </div>
            ))}
            {loading && <LoadingSpinner />}
            {hasMore && !loading && (
              <button className="notice-list__load-more-btn" onClick={fetchNoticeList}>
                더보기
              </button>
            )}
            {!hasMore && <p className="notice-list__no-more-text">모든 공지사항을 확인했습니다.</p>}
          </div>
        ) : (
          <p className="notice-list__no-notice">등록된 공지사항이 없습니다.</p>
        )}
      </div>
    </div>
  );
};

export default NoticeList;

const LoadingSpinner = () => {
  return (
    <div className="loading-spinner">
      <div className="loading-spinner__spinner"></div>
    </div>
  );
};
